import { CopyIcon } from '@components/Icons';
import NapsterSpinner from '@components/NapsterSpinner';
import { ToolbarButton } from '@components/ToolbarButton/ToolbarButton';
import { useGenerationState } from '@hooks/storeHooks/useGenerationState';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Safari needs a real text node to draw a caret; strip before save/copy. */
const ZWSP = '\u200B';

const stripLyricsEditorPlaceholders = (value: string) =>
  value.replace(/\u200B/g, '').replace(/\uFEFF/g, '');

const lyricsTextFromEditor = (el: HTMLDivElement) =>
  stripLyricsEditorPlaceholders(el.innerText || '');

const isEditorLyricsEmpty = (el: HTMLDivElement) =>
  lyricsTextFromEditor(el).trim().length === 0;

/** WebKit often applies focus/selection before the empty placeholder paints; defer hard. */
const scheduleLyricsCaret = (fn: () => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setTimeout(fn, 0);
    });
  });
};

interface LyricsArtifactProps {
  lyrics: string | null;
  title?: string;
  isLoading: boolean;
  className?: string;
  onDragHandleMouseDown?: (e: React.MouseEvent) => void;
  onLyricsChange?: (editedLyrics: string) => void;
  onTitleChange?: (editedTitle: string) => void;
  style?: React.CSSProperties;
}

export const LyricsArtifact: React.FC<LyricsArtifactProps> = ({
  lyrics,
  title = '',
  isLoading,
  className = '',
  onDragHandleMouseDown,
  onLyricsChange,
  onTitleChange,
  style,
}) => {
  const { t } = useTranslation();
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const normalizedTitle = title.trim();
  const titleDisplayText = normalizedTitle
    ? title
    : isLoading
      ? t('generation.generatingTitle')
      : t('generation.tapToAddTitle');
  const contentRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const previousLyricsRef = useRef<string | null>(null);
  const hadLyricsRef = useRef(false);
  const { isRegeneratingLyrics, setIsRegeneratingLyrics } =
    useGenerationState();

  if (lyrics) {
    hadLyricsRef.current = true;
  }

  // Keep chrome (title + formatting bar) when the user clears the editor — lyrics is "" but still editable.
  const showLyricsChrome =
    !isLoading && (lyrics != null || hadLyricsRef.current);

  const setEmptyEditorHtml = (el: HTMLDivElement) => {
    el.replaceChildren(document.createTextNode(ZWSP));
  };

  const placeCaretInLyricsEditor = (el: HTMLDivElement) => {
    el.focus({ preventScroll: true });
    const selection = window.getSelection();
    if (!selection) return;
    try {
      const range = document.createRange();
      if (isEditorLyricsEmpty(el)) {
        const first = el.firstChild;
        const hasZwspText =
          first?.nodeType === Node.TEXT_NODE &&
          (first as Text).data.includes(ZWSP);
        if (!hasZwspText) {
          setEmptyEditorHtml(el);
        }
        const node = el.firstChild;
        if (node?.nodeType === Node.TEXT_NODE) {
          const data = (node as Text).data;
          const i = data.indexOf(ZWSP);
          // Anchor inside the text node — setStartBefore(<br>) still fails to blink on some Safari builds.
          const offset = i >= 0 ? i + 1 : data.length;
          range.setStart(node, Math.min(offset, data.length));
          range.collapse(true);
        } else {
          const br = el.querySelector('br');
          if (br) {
            range.setStartBefore(br);
            range.collapse(true);
          } else {
            range.setStart(el, 0);
            range.collapse(true);
          }
        }
      } else {
        range.selectNodeContents(el);
        range.collapse(false);
      }
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // If the tree is odd, focus alone is still better than nothing
    }
  };
  const handleCopy = () => {
    const fromEditor = contentRef.current
      ? lyricsTextFromEditor(contentRef.current)
      : '';
    const currentContent = fromEditor || lyrics || '';
    if (currentContent.trim()) {
      navigator.clipboard.writeText(currentContent);
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 2000);
    }
  };

  const executeCommandWithCursor = (command: string) => {
    if (!contentRef.current) return;
    contentRef.current.focus();
    document.execCommand(command, false);
  };

  const formatLyricsToHtml = (text: string) => {
    // Convert lyrics to HTML format
    const lines = text.split('\n');
    return lines
      .map((line) => {
        // Check if line is a structure marker (e.g., [Verse 1], [Chorus])
        if (line.trim().match(/^\[.*\]$/)) {
          return `<div class="text-blue-300 font-normal mt-4 mb-2">${line.trim()}</div>`;
        }
        // Regular lyrics line
        return `<div class="leading-[22px]">${line.trim() || '&nbsp;'}</div>`;
      })
      .join('');
  };

  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (!selection || !contentRef.current) return null;

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range) return null;

    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(contentRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    const cursorOffset = preCaretRange.toString().length;

    return cursorOffset;
  };

  const restoreCursorPosition = (cursorOffset: number) => {
    if (!contentRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    try {
      let currentOffset = 0;
      const range = document.createRange();

      const findPosition = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
          const textLength = node.textContent?.length || 0;
          if (currentOffset + textLength >= cursorOffset) {
            range.setStart(node, cursorOffset - currentOffset);
            range.collapse(true);
            return true;
          }
          currentOffset += textLength;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            if (findPosition(node.childNodes[i])) {
              return true;
            }
          }
        }
        return false;
      };

      findPosition(contentRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      console.debug('Could not restore cursor position:', error);
    }
  };

  useEffect(() => {
    // null/undefined = no lyrics artifact yet; empty string is valid (user cleared the editor).
    if (lyrics == null || !contentRef.current) {
      return;
    }

    const currentTextContent = lyricsTextFromEditor(contentRef.current);
    const textContentChanged = currentTextContent !== lyrics;

    if (previousLyricsRef.current !== lyrics && textContentChanged) {
      setIsRegeneratingLyrics(false);

      const cursorOffset = saveCursorPosition();
      const hadFocus = document.activeElement === contentRef.current;

      if (lyrics === '') {
        setEmptyEditorHtml(contentRef.current);
      } else {
        const htmlContent = formatLyricsToHtml(lyrics);
        contentRef.current.innerHTML = htmlContent;
      }
      previousLyricsRef.current = lyrics;

      if (hadFocus) {
        scheduleLyricsCaret(() => {
          if (!contentRef.current) return;
          if (lyrics === '') {
            placeCaretInLyricsEditor(contentRef.current);
          } else if (cursorOffset !== null) {
            restoreCursorPosition(cursorOffset);
          }
        });
      }
    } else if (previousLyricsRef.current !== lyrics && !textContentChanged) {
      previousLyricsRef.current = lyrics;
      // First sync: lyrics is already "" but the div mounted empty (no prior prop transition).
      if (lyrics === '' && contentRef.current) {
        setEmptyEditorHtml(contentRef.current);
      }
    }
  }, [lyrics]);

  useEffect(() => {
    if (!isEditingTitle) {
      setTitleDraft(title || '');
    }
  }, [title, isEditingTitle]);

  useEffect(() => {
    if (isEditingTitle) {
      requestAnimationFrame(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
      });
    }
  }, [isEditingTitle]);

  const handleTitleSave = () => {
    onTitleChange?.(titleDraft.trim());
    setIsEditingTitle(false);
  };

  const defaultMinHeight = 420;

  return (
    <div className={`relative ${className}`}>
      <style>
        {`
          @keyframes lyrics-gradient-rotation {
            from {
              --rotation: 0deg;
            }
            to {
              --rotation: 360deg;
            }
          }
          .lyrics-gradient-border.animating {
            animation: lyrics-gradient-rotation 1.25s linear infinite;
          }
        `}
      </style>
      {(isLoading || isRegeneratingLyrics) && (
        <>
          <div
            className={`gradient-border lyrics-gradient-border ${
              isLoading || isRegeneratingLyrics ? 'animating' : ''
            }`}
            style={
              {
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                right: '-2px',
                bottom: '-2px',
                pointerEvents: 'none',
                borderRadius: '24px',
                '--rotation': '0deg',
                background:
                  'conic-gradient(from calc(0deg + var(--rotation)), var(--Tertiary, #F6911D) 0deg, var(--Secondary, #FF04FF) 120deg, var(--Primary, #0FF) 240deg, var(--Tertiary, #F6911D) 360deg)',
                zIndex: 1,
              } as React.CSSProperties & { '--rotation': string }
            }
          />
        </>
      )}
      {/* Lyrics Box - Matches Artifacts styling */}
      <div
        className="
          w-72 md:w-[420px]
          bg-white/[0.08] backdrop-blur-[20px]
          border border-white/[0.07]
          rounded-[20px]
          shadow-[0_5.315px_5.315px_0_rgba(0,0,0,0.07),2.658px_2.658px_10.63px_0_rgba(0,0,0,0.01)]
          overflow-hidden
          pointer-events-auto
          flex flex-col
        "
        style={
          style
            ? {
                ...style,
                minHeight:
                  typeof style.height === 'number'
                    ? style.height
                    : style.minHeight ?? defaultMinHeight,
                width: style.width,
              }
            : undefined
        }>
        {/* Header - Drag handle */}
        <div
          className="px-[20px] pt-[20px] pb-[20px] flex items-center justify-between cursor-move"
          onMouseDown={onDragHandleMouseDown}>
          <h3 className="text-white font-normal text-[15px] leading-[22px] flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {t('generation.yourSong')}
          </h3>

          {/* Copy button */}
          {(lyrics?.length ?? 0) > 0 && !isLoading && (
            <div className="relative">
              <button
                onClick={handleCopy}
                className="text-white/80 hover:text-white transition-colors cursor-pointer flex items-center"
                aria-label={t('generation.copyLyrics')}>
                <div className="w-5 h-5">
                  <CopyIcon />
                </div>
              </button>

              {showCopyTooltip && (
                <div className="absolute -top-[30px] left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/80 text-white text-xs rounded whitespace-nowrap">
                  {t('generation.copied')}
                  <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-black/80" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Editing Toolbar */}
        {showLyricsChrome ? (
          <div className="px-[20px] pb-2 border-b border-white/[0.07]">
            <div className="text-white/55 text-[10px] uppercase tracking-[0.12em] font-semibold mb-1">
              {t('generation.songTitle')}
            </div>
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleTitleSave();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setTitleDraft(title || '');
                    setIsEditingTitle(false);
                  }
                }}
                className="w-full bg-transparent text-white text-[16px] leading-[22px] font-normal outline-none border-b border-white/25 pb-1"
                placeholder={t('generation.enterSongTitle')}
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsEditingTitle(true)}
                className="text-white text-[16px] leading-[22px] font-normal text-left hover:text-white/90 transition-colors">
                {titleDisplayText}
              </button>
            )}
          </div>
        ) : (
          <div className="px-[20px] pb-2 border-b border-white/[0.07]">
            <div className="text-white/55 text-[11px] uppercase tracking-[0.12em] font-semibold mb-1">
              {t('generation.songTitle')}
            </div>
            <div className="text-white/80 text-[22px] leading-[28px] font-normal">
              {titleDisplayText}
            </div>
          </div>
        )}

        {showLyricsChrome ? (
          <div className="nodrag nowheel px-[20px] pb-3 flex items-center gap-1 border-b border-white/[0.07]">
            <ToolbarButton
              title={t('toolbar.bold')}
              onClick={() => executeCommandWithCursor('bold')}
              icon="fa-solid fa-bold"
            />
            <ToolbarButton
              title={t('toolbar.italic')}
              onClick={() => executeCommandWithCursor('italic')}
              icon="fa-solid fa-italic"
            />
            <ToolbarButton
              title={t('toolbar.underline')}
              onClick={() => executeCommandWithCursor('underline')}
              icon="fa-solid fa-underline"
            />
            <div className="w-px h-5 bg-white/[0.07] mx-1" />
            <ToolbarButton
              title={t('toolbar.alignLeft')}
              onClick={() => executeCommandWithCursor('justifyLeft')}
              icon="fa-solid fa-align-left"
            />
            <ToolbarButton
              title={t('toolbar.alignCenter')}
              onClick={() => executeCommandWithCursor('justifyCenter')}
              icon="fa-solid fa-align-center"
            />
            <ToolbarButton
              title={t('toolbar.alignRight')}
              onClick={() => executeCommandWithCursor('justifyRight')}
              icon="fa-solid fa-align-right"
            />
          </div>
        ) : (
          /* Spacer when loading to maintain height */
          <div className="px-[20px] pb-3 h-[52px]" />
        )}

        {/* Content */}
        <div
          className="px-[20px] pb-[30px] overflow-y-auto custom-scrollbar nodrag nowheel"
          style={
            style?.height
              ? {
                  maxHeight: Math.max(
                    (typeof style.height === 'number' ? style.height : 0) - 120,
                    160,
                  ),
                }
              : undefined
          }>
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center py-12">
              <NapsterSpinner size="medium" />
              <p className="text-white/60 text-[15px] leading-[22px] font-light mt-4">
                {t('generation.generatingLyrics')}
              </p>
            </div>
          ) : lyrics || hadLyricsRef.current ? (
            <div
              ref={contentRef}
              contentEditable={true}
              role="textbox"
              aria-multiline={true}
              className="nodrag nowheel text-white caret-white whitespace-pre-wrap text-[15px] leading-[22px] font-light min-h-96 outline-none !cursor-text select-text"
              onMouseDown={(event) => {
                // React Flow drags the node on mousedown unless we stop it here; empty WebKit editables were easy to miss-hit.
                event.stopPropagation();
              }}
              onClick={() => {
                const el = contentRef.current;
                if (!el) return;
                if (isEditorLyricsEmpty(el)) {
                  setEmptyEditorHtml(el);
                  scheduleLyricsCaret(() => {
                    if (contentRef.current) {
                      placeCaretInLyricsEditor(contentRef.current);
                    }
                  });
                }
              }}
              onFocus={() => {
                const el = contentRef.current;
                if (!el) return;
                if (isEditorLyricsEmpty(el)) {
                  setEmptyEditorHtml(el);
                  scheduleLyricsCaret(() => {
                    if (contentRef.current) {
                      placeCaretInLyricsEditor(contentRef.current);
                    }
                  });
                }
              }}
              onBlur={() => {
                if (contentRef.current && onLyricsChange) {
                  onLyricsChange(lyricsTextFromEditor(contentRef.current));
                }
              }}
              onInput={() => {
                if (!contentRef.current || !onLyricsChange) return;
                const el = contentRef.current;
                const text = lyricsTextFromEditor(el);
                if (text.trim() === '') {
                  setEmptyEditorHtml(el);
                  scheduleLyricsCaret(() => {
                    if (contentRef.current) {
                      placeCaretInLyricsEditor(contentRef.current);
                    }
                  });
                }
                onLyricsChange(text);
              }}
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                cursor: 'text',
                WebkitUserModify: 'read-write',
              } as React.CSSProperties}
              suppressContentEditableWarning={true}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-white/40 text-[15px] leading-[22px] font-light">
                {t('generation.noLyricsAvailable')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
